/* =========================================================================
   LAZ Skincare — minimal QR encoder

   Byte mode, error correction level M, versions 1 to 10 (up to 213 bytes),
   which comfortably covers a share URL.

   This exists rather than a third-party QR image service because the routine
   URL would otherwise be handed to another company on every print — a
   customer's skincare routine is not ours to leak. Everything here runs in
   the browser and nothing leaves the page.

   Verified module-for-module against the `qrcode` npm package: 369 strings
   spanning versions 1 to 10, each compared under all eight mask patterns —
   2952 matrices, no differences. Mask *selection* is deliberately not
   compared, because it is a quality heuristic rather than a correctness one
   and the two encoders round rule 4 differently; every mask decodes the same.

   window.LazQR.matrix(text, opts) -> { size, version, mask, modules: bool[][] }
   window.LazQR.svg(text, opts) -> SVG markup string
   ========================================================================= */

(function () {
  "use strict";

  /* ---------- GF(256) ---------- */

  const EXP = new Uint8Array(512);
  const LOG = new Uint8Array(256);
  (function initTables() {
    let x = 1;
    for (let i = 0; i < 255; i++) {
      EXP[i] = x;
      LOG[x] = i;
      x <<= 1;
      if (x & 0x100) x ^= 0x11d;
    }
    for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
  })();

  const mul = (a, b) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

  function generatorPoly(degree) {
    let poly = [1];
    for (let i = 0; i < degree; i++) {
      const next = new Array(poly.length + 1).fill(0);
      /* Coefficients run highest degree first, so multiplying by x keeps the
         index and multiplying by α^i moves one place down. Swapping these two
         builds the generator backwards, which still divides cleanly and still
         produces plausible-looking parity — just not the right parity. */
      for (let j = 0; j < poly.length; j++) {
        next[j] ^= poly[j];
        next[j + 1] ^= mul(poly[j], EXP[i]);
      }
      poly = next;
    }
    return poly;
  }

  function ecCodewords(data, count) {
    const gen = generatorPoly(count);
    const res = new Array(data.length + count).fill(0);
    for (let i = 0; i < data.length; i++) res[i] = data[i];

    for (let i = 0; i < data.length; i++) {
      const factor = res[i];
      if (factor === 0) continue;
      for (let j = 0; j < gen.length; j++) {
        res[i + j] ^= mul(gen[j], factor);
      }
    }
    return res.slice(data.length);
  }

  /* ---------- version tables (level M only) ---------- */

  /* [dataCodewords, ecPerBlock, [ [blocks, dataPerBlock], … ] ] */
  const VERSIONS = {
    1: [16, 10, [[1, 16]]],
    2: [28, 16, [[1, 28]]],
    3: [44, 26, [[1, 44]]],
    4: [64, 18, [[2, 32]]],
    5: [86, 24, [[2, 43]]],
    6: [108, 16, [[4, 27]]],
    7: [124, 18, [[4, 31]]],
    8: [154, 22, [[2, 38], [2, 39]]],
    9: [182, 22, [[3, 36], [2, 37]]],
    10: [216, 26, [[4, 43], [1, 44]]],
  };

  const ALIGNMENT = {
    1: [],
    2: [6, 18],
    3: [6, 22],
    4: [6, 26],
    5: [6, 30],
    6: [6, 34],
    7: [6, 22, 38],
    8: [6, 24, 42],
    9: [6, 26, 46],
    10: [6, 28, 50],
  };

  const REMAINDER = { 1: 0, 2: 7, 3: 7, 4: 7, 5: 7, 6: 7, 7: 0, 8: 0, 9: 0, 10: 0 };

  /* ---------- bit stream ---------- */

  function BitBuffer() {
    this.bits = [];
  }
  BitBuffer.prototype.put = function (value, length) {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  };
  BitBuffer.prototype.bytes = function () {
    const out = new Array(Math.ceil(this.bits.length / 8)).fill(0);
    this.bits.forEach((bit, i) => {
      if (bit) out[i >> 3] |= 0x80 >>> i % 8;
    });
    return out;
  };

  function utf8(text) {
    if (typeof TextEncoder === "function") {
      return Array.prototype.slice.call(new TextEncoder().encode(text));
    }
    const encoded = unescape(encodeURIComponent(text));
    const out = new Array(encoded.length);
    for (let i = 0; i < encoded.length; i++) out[i] = encoded.charCodeAt(i);
    return out;
  }

  function pickVersion(byteLength) {
    for (let v = 1; v <= 10; v++) {
      const [dataCodewords] = VERSIONS[v];
      const cci = v < 10 ? 8 : 16;
      const needed = Math.ceil((4 + cci + byteLength * 8) / 8);
      if (needed <= dataCodewords) return v;
    }
    throw new Error("Too much data for a version 10 QR code.");
  }

  function encodeData(text) {
    const data = utf8(text);
    const version = pickVersion(data.length);
    const [dataCodewords, ecPerBlock, groups] = VERSIONS[version];

    const buffer = new BitBuffer();
    buffer.put(4, 4); // byte mode
    buffer.put(data.length, version < 10 ? 8 : 16);
    data.forEach((byte) => buffer.put(byte, 8));

    /* Terminator, then pad to a byte boundary, then the standard pad bytes. */
    const capacityBits = dataCodewords * 8;
    for (let i = 0; i < 4 && buffer.bits.length < capacityBits; i++) buffer.bits.push(0);
    while (buffer.bits.length % 8 !== 0) buffer.bits.push(0);

    const bytes = buffer.bytes();
    const pads = [0xec, 0x11];
    for (let i = 0; bytes.length < dataCodewords; i++) bytes.push(pads[i % 2]);

    /* Split into blocks, compute EC per block, then interleave. */
    const dataBlocks = [];
    const ecBlocks = [];
    let pos = 0;
    groups.forEach(([count, perBlock]) => {
      for (let i = 0; i < count; i++) {
        const block = bytes.slice(pos, pos + perBlock);
        pos += perBlock;
        dataBlocks.push(block);
        ecBlocks.push(ecCodewords(block, ecPerBlock));
      }
    });

    const result = [];
    const maxData = Math.max(...dataBlocks.map((b) => b.length));
    for (let i = 0; i < maxData; i++) {
      dataBlocks.forEach((block) => {
        if (i < block.length) result.push(block[i]);
      });
    }
    for (let i = 0; i < ecPerBlock; i++) {
      ecBlocks.forEach((block) => result.push(block[i]));
    }

    return { version, codewords: result };
  }

  /* ---------- matrix ---------- */

  function buildMatrix(version) {
    const size = version * 4 + 17;
    const modules = Array.from({ length: size }, () => new Array(size).fill(null));
    const reserved = Array.from({ length: size }, () => new Array(size).fill(false));

    const setF = (r, c, value) => {
      modules[r][c] = value;
      reserved[r][c] = true;
    };

    /* Finder patterns and separators. */
    const finder = (row, col) => {
      for (let r = -1; r <= 7; r++) {
        for (let c = -1; c <= 7; c++) {
          const rr = row + r;
          const cc = col + c;
          if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
          const inRing =
            (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
            (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
            (r >= 2 && r <= 4 && c >= 2 && c <= 4);
          setF(rr, cc, inRing);
        }
      }
    };
    finder(0, 0);
    finder(0, size - 7);
    finder(size - 7, 0);

    /* Timing patterns. */
    for (let i = 8; i < size - 8; i++) {
      setF(6, i, i % 2 === 0);
      setF(i, 6, i % 2 === 0);
    }

    /* Alignment patterns, skipping any that would collide with a finder. */
    const centers = ALIGNMENT[version];
    centers.forEach((row) => {
      centers.forEach((col) => {
        const nearFinder =
          (row <= 8 && col <= 8) ||
          (row <= 8 && col >= size - 9) ||
          (row >= size - 9 && col <= 8);
        if (nearFinder) return;
        for (let r = -2; r <= 2; r++) {
          for (let c = -2; c <= 2; c++) {
            setF(row + r, col + c, Math.max(Math.abs(r), Math.abs(c)) !== 1);
          }
        }
      });
    });

    /* Dark module. */
    setF(size - 8, 8, true);

    /* Reserve the format areas (values written later). */
    for (let i = 0; i < 9; i++) {
      if (!reserved[8][i]) setF(8, i, false);
      if (!reserved[i][8]) setF(i, 8, false);
    }
    for (let i = 0; i < 8; i++) {
      if (!reserved[8][size - 1 - i]) setF(8, size - 1 - i, false);
      if (!reserved[size - 1 - i][8]) setF(size - 1 - i, 8, false);
    }

    /* Reserve the version areas. */
    if (version >= 7) {
      for (let i = 0; i < 6; i++) {
        for (let j = 0; j < 3; j++) {
          setF(size - 11 + j, i, false);
          setF(i, size - 11 + j, false);
        }
      }
    }

    return { size, modules, reserved };
  }

  function placeData(matrix, codewords, version) {
    const { size, modules, reserved } = matrix;
    const bits = [];
    codewords.forEach((byte) => {
      for (let i = 7; i >= 0; i--) bits.push((byte >>> i) & 1);
    });
    for (let i = 0; i < REMAINDER[version]; i++) bits.push(0);

    let index = 0;
    let upward = true;

    for (let right = size - 1; right > 0; right -= 2) {
      if (right === 6) right = 5; // the vertical timing column is skipped
      for (let step = 0; step < size; step++) {
        const row = upward ? size - 1 - step : step;
        for (let c = 0; c < 2; c++) {
          const col = right - c;
          if (reserved[row][col]) continue;
          modules[row][col] = index < bits.length ? bits[index] === 1 : false;
          index++;
        }
      }
      upward = !upward;
    }
  }

  const MASKS = [
    (r, c) => (r + c) % 2 === 0,
    (r) => r % 2 === 0,
    (r, c) => c % 3 === 0,
    (r, c) => (r + c) % 3 === 0,
    (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
    (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
    (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
    (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
  ];

  function applyMask(matrix, maskIndex) {
    const { size, modules, reserved } = matrix;
    const mask = MASKS[maskIndex];
    const out = modules.map((row) => row.slice());
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (reserved[r][c]) continue;
        if (mask(r, c)) out[r][c] = !out[r][c];
      }
    }
    return out;
  }

  /* BCH(15,5) format information, EC level M = 0b00. */
  function formatBits(maskIndex) {
    const data = (0b00 << 3) | maskIndex;
    let value = data << 10;
    for (let i = 14; i >= 10; i--) {
      if ((value >>> i) & 1) value ^= 0x537 << (i - 10);
    }
    return ((data << 10) | value) ^ 0x5412;
  }

  /* BCH(18,6) version information. */
  function versionBits(version) {
    let value = version << 12;
    for (let i = 17; i >= 12; i--) {
      if ((value >>> i) & 1) value ^= 0x1f25 << (i - 12);
    }
    return (version << 12) | value;
  }

  /* The 15 format bits are written twice, and each copy runs along its own
     strip. Folding them into one branch put bit 8 onto [8][6] — the vertical
     timing column — quietly corrupting a function pattern. Keep them apart. */
  function writeFormat(modules, size, maskIndex) {
    const bits = formatBits(maskIndex);
    for (let i = 0; i < 15; i++) {
      const bit = ((bits >>> i) & 1) === 1;

      /* Vertical strip: down the left of the top-left finder, then up from
         the bottom-left one. */
      if (i < 6) modules[i][8] = bit;
      else if (i < 8) modules[i + 1][8] = bit;
      else modules[size - 15 + i][8] = bit;

      /* Horizontal strip: in from the top-right finder, then back along the
         underside of the top-left one. */
      if (i < 8) modules[8][size - 1 - i] = bit;
      else if (i === 8) modules[8][7] = bit;
      else modules[8][14 - i] = bit;
    }
  }

  function writeVersion(modules, size, version) {
    if (version < 7) return;
    const bits = versionBits(version);
    for (let i = 0; i < 18; i++) {
      const bit = ((bits >>> i) & 1) === 1;
      const row = Math.floor(i / 3);
      const col = i % 3;
      modules[size - 11 + col][row] = bit;
      modules[row][size - 11 + col] = bit;
    }
  }

  /* ---------- mask penalties ---------- */

  function penalty(modules, size) {
    let score = 0;

    /* Rule 1: runs of five or more of the same colour. */
    const runScore = (get) => {
      let total = 0;
      for (let a = 0; a < size; a++) {
        let run = 1;
        for (let b = 1; b < size; b++) {
          if (get(a, b) === get(a, b - 1)) {
            run++;
          } else {
            if (run >= 5) total += run - 2;
            run = 1;
          }
        }
        if (run >= 5) total += run - 2;
      }
      return total;
    };
    score += runScore((r, c) => modules[r][c]);
    score += runScore((c, r) => modules[r][c]);

    /* Rule 2: 2x2 blocks of one colour. */
    for (let r = 0; r < size - 1; r++) {
      for (let c = 0; c < size - 1; c++) {
        const v = modules[r][c];
        if (v === modules[r][c + 1] && v === modules[r + 1][c] && v === modules[r + 1][c + 1]) {
          score += 3;
        }
      }
    }

    /* Rule 3: finder-like patterns. */
    const pattern1 = [true, false, true, true, true, false, true, false, false, false, false];
    const pattern2 = [false, false, false, false, true, false, true, true, true, false, true];
    const matches = (get, a, b) => {
      const check = (pattern) => {
        for (let i = 0; i < 11; i++) if (get(a, b + i) !== pattern[i]) return false;
        return true;
      };
      return check(pattern1) || check(pattern2);
    };
    for (let r = 0; r < size; r++) {
      for (let c = 0; c + 11 <= size; c++) {
        if (matches((a, b) => modules[a][b], r, c)) score += 40;
        if (matches((a, b) => modules[b][a], r, c)) score += 40;
      }
    }

    /* Rule 4: overall balance of dark modules. */
    let dark = 0;
    for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (modules[r][c]) dark++;
    const percent = (dark * 100) / (size * size);
    score += Math.floor(Math.abs(percent - 50) / 5) * 10;

    return score;
  }

  /* ---------- public ---------- */

  function matrix(text, options = {}) {
    const { version, codewords } = encodeData(text);
    const base = buildMatrix(version);
    placeData(base, codewords, version);

    /* All eight masks decode identically; scoring only picks the tidiest one.
       Forcing one is for the verification harness, which compares against a
       reference encoder mask by mask rather than trusting both to break
       scoring ties the same way. */
    const forced = options.mask;
    const first = forced == null ? 0 : forced;
    const last = forced == null ? 7 : forced;

    let best = null;
    for (let m = first; m <= last; m++) {
      const candidate = applyMask(base, m);
      writeFormat(candidate, base.size, m);
      writeVersion(candidate, base.size, version);
      const score = penalty(candidate, base.size);
      if (!best || score < best.score) best = { score, modules: candidate, mask: m };
    }

    return { size: base.size, version, mask: best.mask, modules: best.modules };
  }

  /* A path of filled squares scales cleanly and prints crisply. */
  function svg(text, options = {}) {
    const { size, modules } = matrix(text);
    const quiet = options.quiet == null ? 4 : options.quiet;
    const total = size + quiet * 2;

    let path = "";
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (modules[r][c]) path += `M${c + quiet} ${r + quiet}h1v1h-1z`;
      }
    }

    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}" ` +
      `shape-rendering="crispEdges" role="img" aria-label="${
        options.label ? String(options.label).replace(/[&<>"]/g, "") : "QR code"
      }">` +
      `<rect width="${total}" height="${total}" fill="#ffffff"/>` +
      `<path d="${path}" fill="#000000"/>` +
      `</svg>`
    );
  }

  const api = { matrix, svg };

  if (typeof window !== "undefined") window.LazQR = api;
  if (typeof module !== "undefined" && module.exports) module.exports = api;
})();
